import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import {
  useConversationAssigneeGroupsManagement,
  useCreateConversationAssigneeGroup,
  useUpdateConversationAssigneeGroup,
} from 'src/graphql/entities/conversation-assignee-group';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import {
  AssigneeGroupFormField,
  type AssigneeGroupFormValues,
  getAssigneeGroupDefaults,
  useAssigneeGroupForm,
} from './use-assignee-group-form';

export const ManageAssigneeGroupModal = () => {
  const { closeModal } = useModalMethods(Modals.ManageAssigneeGroup);
  const { isOpened, modalProps } = useModalState(Modals.ManageAssigneeGroup);

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const partnerId = modalProps?.partnerId ?? null;
  const mode = modalProps?.mode;

  const editingGroup = modalProps?.mode === 'edit' ? modalProps.group : undefined;

  const { groups: existingGroups, cacheManager } =
    useConversationAssigneeGroupsManagement(partnerId);
  const { createConversationAssigneeGroup, loading: creating } =
    useCreateConversationAssigneeGroup(cacheManager);
  const { updateConversationAssigneeGroup, loading: updating } =
    useUpdateConversationAssigneeGroup();

  const formMethods = useForm<AssigneeGroupFormValues>({
    defaultValues: getAssigneeGroupDefaults(editingGroup),
    mode: 'onChange',
  });

  useEffect(() => {
    if (!isOpened) return;
    formMethods.reset(getAssigneeGroupDefaults(editingGroup));
  }, [isOpened, editingGroup, formMethods]);

  const formFields = useAssigneeGroupForm({
    formMethods,
    partnerId,
    existingGroups: existingGroups ?? [],
    editingId: editingGroup?.id,
  });

  const onSubmit = formMethods.handleSubmit(async (values) => {
    if (partnerId === null) return;

    const trimmedName = values[AssigneeGroupFormField.Name].trim();

    try {
      if (mode === 'edit' && editingGroup) {
        await updateConversationAssigneeGroup(
          {
            id: editingGroup.id,
            name: trimmedName,
            description: values[AssigneeGroupFormField.Description],
            icon: values[AssigneeGroupFormField.Icon],
          },
          editingGroup,
        );
        showSuccessAlert('Group updated');
      } else {
        await createConversationAssigneeGroup({
          partnerId,
          name: trimmedName,
          description: values[AssigneeGroupFormField.Description] || undefined,
          icon: values[AssigneeGroupFormField.Icon] || undefined,
        });
        showSuccessAlert('Group created');
      }
      closeModal();
    } catch (err) {
      showErrorAlert(
        err instanceof Error ? err.message : 'Failed to save group. Please try again.',
      );
    }
  });

  const handleClose = useCallback(() => {
    if (creating || updating) return;
    closeModal();
  }, [closeModal, creating, updating]);

  const isMutating = creating || updating;
  const isSubmitDisabled = isMutating || !formMethods.formState.isValid;

  return (
    <Dialog
      open={Boolean(isOpened)}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="manage-assignee-group-modal"
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit Assignee Group' : 'Add Assignee Group'}
      </DialogTitle>
      <DialogContent>
        <GenericForm<AssigneeGroupFormValues> formMethods={formMethods} gap={2} pt={1}>
          {formFields}
        </GenericForm>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isMutating}
          data-testid="manage-assignee-group-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSubmitDisabled}
          onClick={onSubmit}
          data-testid="manage-assignee-group-submit"
        >
          {mode === 'edit' ? 'Save changes' : 'Create group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
